import { useFpCampaignCartCalculate } from '@cart-services/hooks/useFpCampaignCartCalculate'
import { useFpCampaignCartStore } from '@cart-services/hooks/useFpCampaignCartStore'
import { useFpCampaignCouponService } from '@cart-services/hooks/useFpCampaignCouponService'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { FormControlLabel, type FormControlLabelProps, Radio } from '@mui/material'
import { Alert } from '@pets-ui/Alert/Alert'
import { Divider } from '@pets-ui/Divider/Divider'
import { HStack } from '@pets-ui/HStack/HStack'
import { GiftFilled } from '@pets-ui/icons'
import { Text } from '@pets-ui/Text/Text'
import { colors } from '@pets-ui/tokens/colors.stylex'
import { VStack } from '@pets-ui/VStack/VStack'
import * as stylex from '@stylexjs/stylex'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useContextCampaignCart } from '../CartProvider/CampaignCartProvider'

const IconAlert = () => {
  const { t } = useTranslation()

  return (
    <HStack gap={2} alignItems="center">
      <GiftFilled fontSize="16px" color={colors.grey800} />
      <Text variant="subtitle" color={colors.grey800}>{t('giveawayDelivery.title')}</Text>
    </HStack>
  )
}

const baseStyles = stylex.create({
  wrapper: {
    padding: '8px',
    borderColor: colors.yellow500,
    borderRadius: '8px',
    borderStyle: 'solid',
    borderWidth: '1px',
    gap: '8px',
    display: 'flex',
    flexDirection: 'column',
  },
  divider: {
    margin: 0,
    color: colors.yellow500,
  },
})

export const CartGiveawayDeliveryOptions = () => {
  const { campaignId, countryCode, campaignCode } = useContextCampaignCart()
  const { destructuredData: exhibitorSettings } = useApiCampaignActivityInfo(campaignCode)
  const [cartValues, setCartValues] = useFpCampaignCartStore(campaignId)
  const { regularGiveaways } = useFpCampaignCouponService(campaignId, countryCode, campaignCode)
  const { destructuredData: calculateResult } = useFpCampaignCartCalculate(campaignId, campaignCode)
  const { t } = useTranslation()

  const handleChange = (isGiveawayDispatch: boolean): FormControlLabelProps['onChange'] => () => {
    setCartValues(prev => ({
      ...prev,
      isGiveawayDispatch,
    }))
  }
  const totalAmount = calculateResult?.total_after_discount ?? 0

  const hasAnyRegularGiveawayApplied = useMemo(() => {
    if (!regularGiveaways) return false
    return regularGiveaways.flatMap(giveaway => giveaway.criterions)
      .some(criterion => totalAmount >= (criterion.criterion_amount ?? 0))
  }, [regularGiveaways, totalAmount])

  const shouldShowCartGiveawayDeliveryOptions = hasAnyRegularGiveawayApplied && (exhibitorSettings?.enable_delivery_giveaway || exhibitorSettings?.enable_pickup_giveaway)
  // NOTE: NOTE: 在 2503 實作，手動輸入的優惠券，含有 giveaways 的優惠券，但目前後端沒有回傳 giveaways，所以先註解
  // const manualInputCoupons = calculateResult?.applied_coupons.flatMap(appliedCoupon => appliedCoupon.criterions).filter(criterion => cartValues.coupon.manualInputCouponIds.includes(criterion.coupon_id))
  // const shouldDisablePickupRadio = manualInputCoupons?.some(criterion => criterion.giveaways?.length)

  if (!exhibitorSettings || cartValues.isGiveawayDispatch === null) return null

  return shouldShowCartGiveawayDeliveryOptions && (
    <Alert rootStyle={baseStyles.wrapper} type="warning" icon={<IconAlert />}>
      <VStack width="100%" gap={2}>
        <Divider rootStyle={baseStyles.divider} />
        <VStack gap={2}>
          {exhibitorSettings.enable_delivery_giveaway && (
            <FormControlLabel
              sx={{ height: 22 }}
              control={
                <Radio color="default" size="small" checked={cartValues.isGiveawayDispatch} />
              }
              label={<Text color={colors.grey800}>{t('giveawayDelivery.1', { defaultValue: '滿額禮與購買商品一起寄送' })}</Text>}
              onChange={handleChange(true)}
            />
          )}
          {exhibitorSettings.enable_pickup_giveaway && (
            <FormControlLabel
              sx={{ height: 22 }}
              control={
                <Radio color="default" size="small" checked={!cartValues.isGiveawayDispatch} />
              }
              label={<Text color={colors.grey800}>{t('giveawayDelivery.0', { defaultValue: '我要現場排隊，領取滿額禮' })}</Text>}
              onChange={handleChange(false)}
            />
          )}
        </VStack>
      </VStack>
    </Alert>
  )
}
